// Period Briefing widget — hours per department / UZK split / CAO toeslag
// breakdown for ANY previous ISO week. Renamed from "Last Week Briefing"
// in iter63: planners frequently want to retrace older weeks (e.g. for
// audits, payroll cross-checks, or to compare two recent weeks side by
// side), so the widget now exposes prev/next navigation and quick-jump
// buttons instead of being hard-coded to the most recent completed week.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { CalendarDays, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { BRAND } from './_constants';
import { previousIsoWeek, fmtMins } from './_helpers';
import { safeFetchJson } from '@/utils/safeFetch';

// Apply ``offsetWeeks`` to an ISO week id (positive = future).
const shiftIsoWeek = (weekId, offsetWeeks) => {
  const m = /^(\d{4})-W(\d{2})$/.exec(weekId || '');
  if (!m) return weekId;
  const year = Number(m[1]);
  const wk = Number(m[2]);
  // Anchor on Thursday of the source week (ISO 8601 anchor day).
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Dow = jan4.getUTCDay() || 7;
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - (jan4Dow - 1) + (wk - 1) * 7);
  const target = new Date(monday);
  target.setUTCDate(monday.getUTCDate() + offsetWeeks * 7 + 3); // +3 → Thursday
  const ys = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((target - ys) / 86400000 + 1) / 7);
  return `${target.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
};

export const BriefingWidget = () => {
  const [weekId, setWeekId] = useState(() => previousIsoWeek());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Quick-jump shortcuts: how many weeks back from the most recent
  // completed week. The "Last week" button always re-aligns the picker
  // to the rolling baseline so users can recover after navigating away.
  const lastWeek = useMemo(() => previousIsoWeek(), []);
  const quickJumps = useMemo(() => ([
    { label: 'Last week', target: lastWeek },
    { label: '2w ago', target: shiftIsoWeek(lastWeek, -1) },
    { label: '4w ago', target: shiftIsoWeek(lastWeek, -3) },
    { label: '12w ago', target: shiftIsoWeek(lastWeek, -11) },
  ]), [lastWeek]);

  useEffect(() => {
    const tok = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    if (!tok) { setLoading(false); return; }
    setLoading(true);
    safeFetchJson(`${API_URL}/api/planning-v2/dash/briefing/${weekId}`, { token: tok })
      .then(d => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [weekId, API_URL]);

  if (!data) return null;

  const empty = !data.total_minutes;

  return (
    <Card data-testid="briefing-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <CalendarDays className="h-5 w-5" style={{ color: BRAND.cyan }} />
            Period Briefing — {weekId}
          </CardTitle>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Button
              variant="outline" size="icon" className="h-8 w-8"
              onClick={() => setWeekId(shiftIsoWeek(weekId, -1))}
              data-testid="briefing-prev-week"
              aria-label="Previous week"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Input
              value={weekId}
              onChange={(e) => setWeekId(e.target.value)}
              className="h-8 w-28 text-xs font-mono"
              data-testid="briefing-week-input"
            />
            <Button
              variant="outline" size="icon" className="h-8 w-8"
              onClick={() => setWeekId(shiftIsoWeek(weekId, 1))}
              data-testid="briefing-next-week"
              aria-label="Next week"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Link to={`/planning?week=${weekId}`} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5 ml-1">
              Open <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
        <CardDescription className="text-xs flex items-center gap-3 flex-wrap">
          <span>Hours per department · UZK split · CAO toeslag breakdown</span>
          <span className="flex items-center gap-1" data-testid="briefing-quickjumps">
            {quickJumps.map(q => (
              <Button
                key={q.label}
                variant={weekId === q.target ? 'secondary' : 'ghost'}
                size="sm"
                className="h-6 px-2 text-[10px]"
                onClick={() => setWeekId(q.target)}
                data-testid={`briefing-quick-${q.label.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {q.label}
              </Button>
            ))}
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="h-32 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : empty ? (
          <p className="text-xs text-muted-foreground py-6 text-center">
            No schedule found for {weekId}. Try a different week (e.g. <code>{lastWeek}</code>) or use the navigation arrows.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="rounded-lg border bg-muted/30 p-2.5">
                <p className="text-[11px] text-muted-foreground">Total scheduled</p>
                <p className="text-lg font-bold" style={{ color: BRAND.cyan }}>{fmtMins(data.total_minutes)}</p>
              </div>
              <div className="rounded-lg border bg-muted/30 p-2.5">
                <p className="text-[11px] text-muted-foreground">Assignments</p>
                <p className="text-lg font-bold">{data.assignments}</p>
              </div>
              <div className="rounded-lg border bg-muted/30 p-2.5">
                <p className="text-[11px] text-muted-foreground">CAO toeslag (≥125%)</p>
                <p className="text-lg font-bold" style={{ color: BRAND.amber }}>{fmtMins(data.toeslag_minutes)}</p>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-3 text-xs">
              <div>
                <p className="font-semibold mb-1 text-muted-foreground">Departments</p>
                {data.per_dept.slice(0, 6).map((d) => (
                  <div key={d.name} className="flex justify-between border-b py-1 last:border-0">
                    <span className="truncate pr-2">{d.name}</span>
                    <span className="font-mono">{fmtMins(d.minutes)}</span>
                  </div>
                ))}
              </div>
              <div>
                <p className="font-semibold mb-1 text-muted-foreground">UZK / agency</p>
                {data.per_uzk.slice(0, 6).map((d) => (
                  <div key={d.name} className="flex justify-between border-b py-1 last:border-0">
                    <span className="truncate pr-2">{d.name}</span>
                    <span className="font-mono">{fmtMins(d.minutes)}</span>
                  </div>
                ))}
              </div>
              <div>
                <p className="font-semibold mb-1 text-muted-foreground">CAO buckets</p>
                {data.buckets.map((b) => (
                  <div key={b.percentage} className="flex justify-between border-b py-1 last:border-0">
                    <span>{b.percentage}%</span>
                    <span className="font-mono">{fmtMins(b.minutes)}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
